import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { companyService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import {
  getAuthItem,
  getAuthJson,
  setAuthJson,
} from "../../../utils/authStorage";
import {
  normalizeSalaryStructureMethod,
  SALARY_STRUCTURE_METHOD_CHANGED_EVENT,
} from "../../../utils/salaryStructureMethod";
import { DsBadge, DsButton, DsSpinner } from "../../../design-system";

function resolveCompanyId() {
  const direct = getAuthItem("companyId");
  if (direct && direct !== "null" && direct !== "undefined") {
    const n = Number(direct);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const currentUser = getAuthJson("currentUser");
  if (currentUser?.companyId) return Number(currentUser.companyId);
  if (currentUser?.company_id) return Number(currentUser.company_id);
  const companyData = getAuthJson("companyData");
  if (companyData?.id) return Number(companyData.id);
  if (companyData?.companyId) return Number(companyData.companyId);
  try {
    const legacyUser = JSON.parse(localStorage.getItem("user") || "null");
    if (legacyUser?.companyId) return Number(legacyUser.companyId);
  } catch {
    /* ignore */
  }
  return 0;
}

function SettingRow({
  label,
  name,
  options,
  value,
  savedValue,
  disabled,
  saving,
  onChange,
  onSave,
}) {
  const dirty = value !== savedValue;
  return (
    <div className="flex flex-col gap-3 border-b border-[hsl(var(--ds-border))] py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="ds-body-lg font-medium text-[hsl(var(--ds-fg))]">{label}</div>
        <div className="mt-1.5">
          {dirty ? (
            <DsBadge tone="warning">Unsaved changes</DsBadge>
          ) : (
            <DsBadge tone="success">Saved</DsBadge>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-4">
          {options.map((opt) => (
            <label
              key={String(opt.value)}
              className={`inline-flex items-center gap-2 ds-body ${
                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
            >
              <input
                type="radio"
                name={name}
                className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                checked={value === opt.value}
                disabled={disabled || saving}
                onChange={() => onChange(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>

        <DsButton
          type="button"
          size="sm"
          disabled={disabled || saving || !dirty}
          loading={saving}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save"}
        </DsButton>
      </div>
    </div>
  );
}

/**
 * Organisation payroll defaults (min wages + salary structure method).
 * One-row Save UI — used by Payroll → General Settings.
 */
export default function EnterprisePayrollDefaultsRows({
  canEdit: canEditProp = null,
  compact = false,
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canEdit =
    canEditProp != null
      ? !!canEditProp
      : isAdmin ||
        hasScreenAccess("configuration", "settings", "edit") ||
        hasScreenAccess("payroll", "payroll_settings", "edit");

  const [loading, setLoading] = useState(true);
  const [savingMw, setSavingMw] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [followMinimumWages, setFollowMinimumWages] = useState(false);
  const [savedFollow, setSavedFollow] = useState(false);
  const [salaryMethod, setSalaryMethod] = useState("CTC");
  const [savedSalaryMethod, setSavedSalaryMethod] = useState("CTC");
  const [companyId] = useState(() => resolveCompanyId());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!companyId) {
        setLoading(false);
        toast.error("Company not found. Please sign in again.");
        return;
      }
      try {
        setLoading(true);
        const res = await companyService.getMinimumWagesSetting(companyId);
        const follow = !!(
          res?.data?.followMinimumWages ?? res?.followMinimumWages
        );
        const method = normalizeSalaryStructureMethod(
          res?.data?.defaultSalaryStructureMethod ??
            res?.defaultSalaryStructureMethod
        );
        if (!cancelled) {
          setFollowMinimumWages(follow);
          setSavedFollow(follow);
          setSalaryMethod(method);
          setSavedSalaryMethod(method);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || "Failed to load settings");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const patchCompanyCache = (patch) => {
    const cached = getAuthJson("companyData");
    if (cached && typeof cached === "object") {
      setAuthJson("companyData", { ...cached, ...patch });
    }
  };

  const handleSaveMw = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to update Settings.");
      return;
    }
    if (!companyId) {
      toast.error("Company not found.");
      return;
    }
    try {
      setSavingMw(true);
      const res = await companyService.updateMinimumWagesSetting(companyId, {
        followMinimumWages,
      });
      const value = !!(
        res?.data?.followMinimumWages ?? res?.followMinimumWages
      );
      setSavedFollow(value);
      setFollowMinimumWages(value);
      patchCompanyCache({ followMinimumWages: value });
      window.dispatchEvent(
        new CustomEvent("pacehrm:minimum-wages-setting-changed", {
          detail: { followMinimumWages: value },
        })
      );
      toast.success(
        value
          ? "Minimum wages enabled. Configure amounts under Masters → Category."
          : "Minimum wages disabled."
      );
    } catch (err) {
      toast.error(err?.message || "Failed to save setting");
    } finally {
      setSavingMw(false);
    }
  };

  const handleSaveMethod = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to update Settings.");
      return;
    }
    if (!companyId) {
      toast.error("Company not found.");
      return;
    }
    try {
      setSavingMethod(true);
      const method = normalizeSalaryStructureMethod(salaryMethod);
      const res = await companyService.updateMinimumWagesSetting(companyId, {
        defaultSalaryStructureMethod: method,
      });
      const saved = normalizeSalaryStructureMethod(
        res?.data?.defaultSalaryStructureMethod ?? method
      );
      setSalaryMethod(saved);
      setSavedSalaryMethod(saved);
      patchCompanyCache({ defaultSalaryStructureMethod: saved });
      window.dispatchEvent(
        new CustomEvent(SALARY_STRUCTURE_METHOD_CHANGED_EVENT, {
          detail: { defaultSalaryStructureMethod: saved },
        })
      );
      toast.success("Default salary structure method saved.");
    } catch (err) {
      toast.error(err?.message || "Failed to save setting");
    } finally {
      setSavingMethod(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center ${
          compact ? "min-h-[6rem]" : "min-h-[12rem]"
        }`}
      >
        <DsSpinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className={compact ? "" : "px-0 py-0"}>
      <SettingRow
        label="Are you following Minimum Wages at your organisation?"
        name="followMinimumWages"
        options={[
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]}
        value={followMinimumWages}
        savedValue={savedFollow}
        disabled={!canEdit}
        saving={savingMw}
        onChange={setFollowMinimumWages}
        onSave={handleSaveMw}
      />

      <SettingRow
        label="Default Salary Structure method"
        name="defaultSalaryStructureMethod"
        options={[
          { value: "CTC", label: "CTC" },
          { value: "GROSS", label: "Gross" },
          { value: "BOTH", label: "Both" },
        ]}
        value={salaryMethod}
        savedValue={savedSalaryMethod}
        disabled={!canEdit}
        saving={savingMethod}
        onChange={setSalaryMethod}
        onSave={handleSaveMethod}
      />
    </div>
  );
}
